import { api } from '../api';

/**
 * As chamadas da repetição na Interpretação (onda 323, RN-260).
 *
 * Os caminhos vêm escritos por inteiro, como nas outras chamadas (o teste
 * `26` procura a rota no fonte da tela). O lançamento dos pontos novos NÃO
 * mora aqui: usa as rotas de sempre de cada ficha — o banco põe a linha na
 * tentativa aberta.
 */

/** As fichas que repetem (as mesmas da `FICHAS` da API). */
export type FichaDaRepeticao =
  | 'umidade' | 'limite-liquidez' | 'limite-plasticidade' | 'granulometria'
  | 'massa-especifica' | 'compactacao' | 'isc';

export interface PendenciaDaRepeticao { codigo: string; texto: string; trava: boolean }

export interface RepeticaoAberta { numero: number; abertaEm: string; abertaPor: string | null }

/**
 * O estado da repetição de uma ficha. `inicial` e `atual` têm a forma do
 * GET da própria ficha (`cabecalho`, `pontos`…): `inicial` é a tentativa
 * vigente; `atual`, a aberta (ou `null`).
 */
export interface EstadoDaRepeticao<F = any> {
  vigente: number;
  aberta: RepeticaoAberta | null;
  inicial: F;
  atual: F | null;
  pendenciasDaRepeticao: PendenciaDaRepeticao[];
  /** Só orienta a tela; quem decide é o banco, na hora de adotar. */
  podeAdotar: boolean;
}

export interface TentativaAnterior<F = any> {
  numero: number;
  situacao: 'encerrada' | 'desistida' | string;
  abertaEm: string | null;
  abertaPor: string | null;
  encerradaEm: string | null;
  encerradaPor: string | null;
  motivo: string | null;
  retrato: F;
  /**
   * `true`: o retrato não foi gravado quando a tentativa saiu (tentativa
   * antiga, ou desistida pelo cancelamento) e foi relido agora, com as
   * Condições de hoje. `false`: é a ficha como estava ao sair.
   */
  recalculado: boolean;
}

export interface LinhaDaTrilha {
  datahora: string;
  usuario: string | null;
  perfil: string | null;
  etapa: string;
  tabela: string;
  operacao: string;
  campos: string[] | null;
  antes: Record<string, unknown> | null;
  depois: Record<string, unknown> | null;
  motivo: string | null;
  tentativa: number | null;
}

export const verRepeticao = (id: string, ficha: FichaDaRepeticao) =>
  api<EstadoDaRepeticao>(`/ensaios/${id}/repeticao?ficha=${ficha}`);

export const abrirRepeticao = (id: string) =>
  api<{ tentativa: number }>(`/ensaios/${id}/repeticao`, { metodo: 'POST' });

export const adotarRepeticao = (id: string, motivo: string) =>
  api<{ tentativa: number; iscs: string[] }>(`/ensaios/${id}/repeticao/adotar`,
    { metodo: 'POST', corpo: { motivo } });

export const desistirRepeticao = (id: string, motivo?: string) =>
  api<void>(`/ensaios/${id}/repeticao/desistir`,
    { metodo: 'POST', corpo: motivo ? { motivo } : {} });

export const verTentativas = (id: string, ficha: FichaDaRepeticao) =>
  api<TentativaAnterior[]>(`/ensaios/${id}/tentativas?ficha=${ficha}`);

export const verTrilhaDoEnsaio = (id: string) =>
  api<LinhaDaTrilha[]>(`/ensaios/${id}/trilha`);
